import {LswRequest} from '@amplify/athena-models';
import API from '../../../APIs/API';
import lswRequest from '../../../APIs/fixtures/lswRequest';
import {Parameter, ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {ResponseLSWJob, ResponseLSW, ResponseLSWPoll} from '../../types';
import {AgeComputedRounded} from '../../../Core/helpers/computed/Age';
import {TemplateDataFields} from '../../ContactForm/EmailEditor/EmailEditor';
import {OutListItem, LswQuoteData} from './types';

export async function pollLSW(id: string): Promise<ResponseLSWPoll> {
    let response: ResponseLSWPoll | null = null;

    do {
        response = await API.Call<ResponseLSWPoll>(`v2/illustrations/lsw/${id}`, 'GET');
        if (response?.data?.status === 'FAILED') {
            throw new Error(`LSW request failed: ${response?.data.message}`);
        }
        if (response?.data?.status !== 'COMPLETED') {
            await new Promise((resolve) => setTimeout(resolve, 5000));
        }
    } while (response?.data.status !== 'COMPLETED');
    return response;
}

const mapLswResult = (data: ResponseLSW, faceAmount: number, product: string, premiumFrequency: string) => {
    const OUT_INITIAL_FACE = 'Initial Face Amount';
    const OUT_LAPSE_YEAR = 'Lapse Year';
    const OUT_INITIAL_MODAL_PREMIUM = 'Modal Premium';

    const {Models} = data;

    const quoteResp = Models.find((x) => x.Name === 'Current');
    const policyValuesTable = quoteResp?.Tables.find((table) => table.Name === 'PolicyValues');
    const policyValuesMap = new Map(policyValuesTable?.Fields.map((f) => [f.Name, f.Values[0]]));

    const policyArraysTable = quoteResp?.Tables.find((table) => table.Name === 'PolicyArrays');
    const accumulatedAgeRows = policyArraysTable?.Fields.find((f) => f.Name === 'Age')?.Values as number[];
    const cashValueRows = policyArraysTable?.Fields.find((f) => f.Name === 'Accumulated Value')?.Values as number[];
    const ageToCashValueMap = new Map(cashValueRows.map((cashValue, index) => [accumulatedAgeRows[index], cashValue]));

    const modalPremiumAmount = policyValuesMap.get(OUT_INITIAL_MODAL_PREMIUM) as number;
    const initialDeathBenefit = policyValuesMap.get(OUT_INITIAL_FACE) as number;
    const lapseYear = policyValuesMap.get(OUT_LAPSE_YEAR) as number;

    const monthsMapper = {Annual: 12, 'Semi-Annual': 6, Quarterly: 3, Monthly: 1};
    const initialMonthlyPremium = modalPremiumAmount / monthsMapper[premiumFrequency as keyof typeof monthsMapper];
    const initialAnnualPremium = initialMonthlyPremium * 12;
    const outList = [] as Array<OutListItem>;

    outList.push({
        name: 'Carrier',
        type: 'string',
        value: 'National Life Group',
        fieldName: TemplateDataFields.carrier,
    });

    outList.push({name: 'Initial annual premium', value: initialAnnualPremium, type: 'currency'});
    outList.push({
        name: 'Initial monthly premium',
        value: initialMonthlyPremium,
        type: 'currency',
        fieldName: TemplateDataFields.monthlyPremium,
    });

    outList.push({name: 'Product name', value: product, type: 'string', fieldName: TemplateDataFields.productName});
    outList.push({
        name: 'Initial death benefit',
        value: initialDeathBenefit,
        type: 'currency',
        fieldName: TemplateDataFields.faceAmount,
    });

    outList.push({name: 'Lapse year', value: lapseYear, type: 'year'});

    [60, 65, 70].forEach((age, index) => {
        const cashValue = ageToCashValueMap.get(age);
        if (cashValue) {
            outList.push({
                name: `Cash value at age ${age}`,
                value: cashValue,
                type: 'year',
                fieldName: index === 2 ? TemplateDataFields.cashValue : undefined,
            });
        }
    });

    if (data.Illustrations) {
        outList.push({
            name: 'Illustration',
            value: data.Illustrations,
            type: 'string',
        });
    }

    // add extra data for graphs
    policyArraysTable?.Fields.forEach((field) => {
        switch (field.Name) {
            case 'Accumulated Value':
                outList.push({
                    name: 'Accumulated Value',
                    type: 'year',
                    value: field.Values,
                    fieldName: TemplateDataFields.cashValue,
                });
                break;
            case 'Surrender Value':
                outList.push({
                    name: 'Surrender Value',
                    type: 'year',
                    value: field.Values,
                    fieldName: TemplateDataFields.cashValue,
                });
                break;
            case 'Net Death Benefit':
                outList.push({
                    name: 'Net Death Benefit',
                    type: 'year',
                    value: field.Values,
                    fieldName: TemplateDataFields.cashValue,
                });
                break;
            default:
        }
    });

    return {
        faceAmount,
        monthlyPremium: initialMonthlyPremium,
        productName: product,
        out: outList,
    };
};

const validateLswApiPayload = (
    parameterStore: ParameterStore,
): {valid: true; quoteData: LswQuoteData} | {valid: false; error: string} => {
    const rateClassMapper = {
        EliteNonTobacco: 'Elite_NT',
        PreferredNonTobacco: 'Preferred_NT',
        ExpressStandardNonTobacco1: 'Express_Standard_NT1',
        ExpressStandardNonTobacco2: 'Express_Standard_NT2',
        StandardNonTobacco: 'Standard_NT',
        PreferredTobacco: 'Preferred_Tobacco',
        StandardTobacco: 'Standard_Tobacco',
        ExpressStandardTobacco: 'Express_Standard_Tobacco',
    } as any;

    const premiumFrequencyMapper = {
        annual: 'Annual',
        semiannual: 'Semi - Annual',
        quarterly: 'Quarterly',
        monthly: 'Monthly',
    } as any;

    const SPECIFY_AMOUNT = 'Specify_Amount';

    // basic information
    const firstName = parameterStore.approval.firstName.value || 'First';
    const lastName = parameterStore.approval.lastName.value || 'Last';
    const bday = parameterStore.approval.bday.value;
    const bmonth = parameterStore.approval.bmonth.value;
    const byear = parameterStore.approval.byear.value;
    const nearestAge = AgeComputedRounded(bday, bmonth, byear);
    const gender = parameterStore.approval.gender.value;
    const state = parameterStore.approval.state.value;
    // Basic Section
    const tableRating = parameterStore.quote.tableRatingLSWIUL2.value;
    const flatExtra = parameterStore.quote.flatExtra.value;
    const flatExtraPerm = parameterStore.quote.flatExtraPerm.value;
    const includeReport = parameterStore.quote.includeReportLsw.value;
    const rateClass = rateClassMapper[parameterStore.quote.healthRatingLSWIUL.value];

    // Coverage and Premium
    const faceAmount = parameterStore.quote.specifiedFaceAmountLSW.value;
    const premiumFrequency = premiumFrequencyMapper[parameterStore.quote.premiumFrequency.value];
    // Coverage and Premium Details
    const complianceTest = parameterStore.quote.complianceTestLSWIUL.value;
    const mecAvoidance = parameterStore.quote.mecAvoidanceLSWIUL.value;

    const deathBenefitOption = parameterStore.quote.deathBenefitOptionLSWIUL.value;
    // replace 0 with null
    const deathBenefitOptionChangeAge = parameterStore.quote.deathBenefitChangeAgeLSW.value || null;
    if (deathBenefitOptionChangeAge && deathBenefitOptionChangeAge < nearestAge) {
        return {valid: false, error: 'Death Benefit Option Change Age cannot be less than Age'};
    }

    const solveTarget = parameterStore.quote.solveTargetLSWIUL.value;
    const firstYearLumpSum = parameterStore.quote.firstYearLumpSumLSWIUL.value || null;
    const deductFirstModalPremiumFromLumpSum = parameterStore.quote.deductFirstModalPremiumFromLumpSum.value;

    // Policy Rider
    const abrChronicIllness = parameterStore.quote.ABRChronicIllness.value;
    const abrTerminalIllness = parameterStore.quote.ABRTerminalIllness.value;
    const abrCriticalIllness = parameterStore.quote.ABRCriticalIllness.value;
    const abrCriticalInjury = parameterStore.quote.ABRCriticalInjury.value;
    const abrSummaryAge = parameterStore.quote.ABRSummaryAge.value;
    const deathBenefitProtection = parameterStore.quote.deathBenefitProtection.value;
    const childrensTerm = parameterStore.quote.ChildrensTerm.value;
    const waiverOfPremium = parameterStore.quote.WaiverofPremium.value;
    const waiverOfPremiumOption = parameterStore.quote.WaiverofPremiumOption.value;
    const specifiedAmount = parameterStore.quote.specifiedAmount.value;

    const premiumSolveInfo = [1, 2, 3, 4, 5]
        .map((num) => {
            const premiumSolveType = (
                parameterStore.quote[`lswPremiumSolveType${num}` as keyof typeof parameterStore.quote] as Parameter
            ).value;
            const amount = (
                parameterStore.quote[`lswPremiumAmount${num}` as keyof typeof parameterStore.quote] as Parameter
            ).value;
            const start = (
                parameterStore.quote[`lswPremiumStart${num}` as keyof typeof parameterStore.quote] as Parameter
            ).value;
            const end = (parameterStore.quote[`lswPremiumEnd${num}` as keyof typeof parameterStore.quote] as Parameter)
                .value;

            return {
                solveType: premiumSolveType,
                amount,
                start,
                end,
            };
        })
        .filter((p) => p.start);

    let lastEnd = 0;
    let maxReached = false;

    // validate premium solve
    /* eslint-disable no-restricted-syntax */
    for (const {start, end, solveType: premiumSolveType, amount} of premiumSolveInfo) {
        if (maxReached) {
            return {valid: false, error: 'Premium ranges not allowed after "M" end'};
        }
        if (amount && premiumSolveType !== SPECIFY_AMOUNT) {
            // Don't error because the UI doesn't let us clear out a numeric field
            console.warn('Amount will not be used for premium solve types other than Specify Amount');
        }
        if (premiumSolveType === SPECIFY_AMOUNT && !amount) {
            return {valid: false, error: 'Amount must be specified if solve type is Specify_Amount'};
        }
        const startNum = parseInt(start, 10);
        if (!startNum) {
            return {valid: false, error: `Invalid premium start time ${start}`};
        }
        const expectedStart = lastEnd + 1;
        if (startNum !== expectedStart) {
            return {valid: false, error: `Invalid premium start time ${startNum}, expected, ${expectedStart}`};
        }

        if (end.trim() === 'M') {
            maxReached = true;
        } else {
            const endNum = parseInt(end, 10);
            if (!endNum) {
                return {valid: false, error: `Invalid premium end time ${end}`};
            }

            lastEnd = endNum;
        }
    }
    /* eslint-enable no-restricted-syntax */

    //Index Strategy
    const uSPacesetterAllocationPercentage = parameterStore.quote.USPacesetterAllocationPercentage.value;
    const sP500AllocationPercentage = parameterStore.quote.SP500AllocationPercentage.value;

    return {
        valid: true,
        quoteData: {
            firstName,
            lastName,
            bday,
            bmonth,
            byear,
            nearestAge,
            gender,
            state,
            tableRating,
            flatExtra,
            flatExtraPerm,
            includeReport,
            rateClass,
            faceAmount,
            premiumFrequency,
            complianceTest,
            mecAvoidance,
            deathBenefitOption,
            deathBenefitOptionChangeAge,
            solveTarget,
            firstYearLumpSum,
            deductFirstModalPremiumFromLumpSum,
            abrChronicIllness,
            abrTerminalIllness,
            abrCriticalIllness,
            abrCriticalInjury,
            abrSummaryAge,
            deathBenefitProtection,
            childrensTerm,
            waiverOfPremium,
            waiverOfPremiumOption,
            specifiedAmount,
            premiumSolveInfo,
            sP500AllocationPercentage,
            uSPacesetterAllocationPercentage,
        },
    };
};

export const lswAPI = async (parameterStore: ParameterStore, flag?: boolean) => {
    const validationResult = validateLswApiPayload(parameterStore);
    if (!validationResult.valid) {
        return {error: validationResult.error};
    }

    const request: LswRequest = JSON.parse(JSON.stringify(lswRequest));

    const {
        firstName,
        lastName,
        bday,
        bmonth,
        byear,
        gender,
        state,
        tableRating,
        flatExtra,
        flatExtraPerm,
        includeReport,
        rateClass,
        faceAmount,
        premiumFrequency,
        complianceTest,
        mecAvoidance,
        deathBenefitOption,
        deathBenefitOptionChangeAge,
        solveTarget,
        firstYearLumpSum,
        deductFirstModalPremiumFromLumpSum,
        abrChronicIllness,
        abrTerminalIllness,
        abrCriticalIllness,
        abrCriticalInjury,
        abrSummaryAge,
        deathBenefitProtection,
        childrensTerm,
        waiverOfPremium,
        waiverOfPremiumOption,
        specifiedAmount,
        premiumSolveInfo,
        sP500AllocationPercentage,
        uSPacesetterAllocationPercentage,
    } = validationResult.quoteData;

    // TODO change product for NY when that API works
    const product = 'FlexLife';

    const {
        Insured: {PrimaryInsured},
        DeathBenefitAndFunding: {
            DeathBenefit: {FaceSolve, DeathBenefitOption},
            TaxAndCompliance,
            Premium,
            Solve,
        },
        Riders,
        TrasType: {Report},
        InterestRates,
    } = request.Transaction;
    PrimaryInsured.FullName = `${firstName} ${lastName}`;
    PrimaryInsured.Gender = state == 'MT' ? 'Unisex' : gender;
    request.Transaction.IllustrationFor.IssueState.Code = state;

    PrimaryInsured.Risk.RateClass = rateClass;

    if (tableRating !== 'None') {
        PrimaryInsured.Risk.TableRating = tableRating.substring(6);
    }

    if (flatExtra) PrimaryInsured.Risk.FlatExtra.Cost = `${flatExtraPerm}`;

    FaceSolve.SolveDetails[0].Amount = `${faceAmount}`;
    TaxAndCompliance.MECAvoidance = mecAvoidance;
    TaxAndCompliance.LifeInsuranceComplianceTest = complianceTest;

    DeathBenefitOption.InitialType = deathBenefitOption;

    if (deathBenefitOptionChangeAge) {
        DeathBenefitOption.ChangeYear = `A${deathBenefitOptionChangeAge}` as unknown as number;
    }

    if (firstYearLumpSum) {
        Premium.FirstYearLumpSum = firstYearLumpSum;
        Premium.DeductFirstModalPremiumfromLumpSum = deductFirstModalPremiumFromLumpSum;
    }

    Premium.PremiumSolve.SolveDetails = premiumSolveInfo.map(({solveType, start, end, amount}) => ({
        Type: solveType,
        Amount: amount || undefined,
        From: start,
        Through: end,
    }));

    Solve.Target = solveTarget;
    Riders.ABRChronicIllness.Opted = abrChronicIllness ? 'Yes' : 'No';
    Riders.ABRTerminalIllness.Opted = abrTerminalIllness ? 'Yes' : 'No';
    Riders.ABRCriticalIllness.Opted = abrCriticalIllness ? 'Yes' : 'No';
    Riders.ABRCriticalInjury.Opted = abrCriticalInjury ? 'Yes' : 'No';
    Riders.DeathBenefitProtection.Opted = deathBenefitProtection ? 'Yes' : 'No';
    Report.OptionalReports.AcceleratedBenefitsRiderSummary.SummaryAges.TerminalIllnessSummaryAge = abrSummaryAge;
    Riders.ChildrensTerm.Opted = childrensTerm ? 'Yes' : 'No';

    if (waiverOfPremium) {
        Riders.WaiverofPremium.Opted = 'Yes';
        Riders.WaiverofPremium.AmountOption = waiverOfPremiumOption;
        Riders.WaiverofPremium.SpecifyAmount = specifiedAmount;
    }
    // eslint-disable-next-line prefer-template
    PrimaryInsured.DateOfBirth = `${bmonth}/${bday}/${byear}`;

    Premium.PremiumMode = premiumFrequency;

    Report.IllustratePDF = includeReport;
    InterestRates.PremiumAllocations.Strategies[0].Allocations = [
        {
            Name: 'SP500PointToPointCapFocus',
            AllocationPct: `${sP500AllocationPercentage}`,
        },
        {
            Name: 'USPacesetterIndexPointtoPointNoCap',
            AllocationPct: `${uSPacesetterAllocationPercentage}`,
        },
    ];
    let data = null;

    if (flag) {
        const postResponse = await API.Call<ResponseLSWJob>('v2/illustrations/lsw', 'POST', {
            poll: true,
            ...request,
        });
        if (!postResponse?.data?.id) {
            return {error: 'Issue starting lsw job'};
        }
        let lswResponse;

        try {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            lswResponse = await pollLSW(postResponse.data.id);
        } catch (error) {
            return {error: 'LSW request failed'};
        }
        data = lswResponse.data.response;
    } else {
        data = await API.Call<ResponseLSW>('v2/illustrations/lsw', 'POST', request);
    }
    if (!data?.Models || !data.Models.length) {
        return {
            error: data?.Messages.map((m: any) => m.MessageText).join(' | ') || 'LSW Request Error',
        };
    }
    let result = mapLswResult(data, faceAmount, product, premiumFrequency);
    result = {...result, ...{requestDetails: request}};
    return result;
};
